import type {ApplicationInsights} from '@microsoft/applicationinsights-web'
import {flushTelemetryBacklog, setTelemetryClient, TELEMETRY_EVENT, trackEvent} from '@/helpers/telemetry'

const OFFLINE_SINCE_STORAGE_KEY = 'sbl.offlineSince'

interface OfflineStart {
    startedAt: number
    route: string
}

const readOfflineStart = (): OfflineStart | undefined => {
    try {
        const raw = localStorage.getItem(OFFLINE_SINCE_STORAGE_KEY)
        return raw ? JSON.parse(raw) as OfflineStart : undefined
    } catch {
        return undefined
    }
}

const writeOfflineStart = (start: OfflineStart | undefined): void => {
    try {
        if (start) {
            localStorage.setItem(OFFLINE_SINCE_STORAGE_KEY, JSON.stringify(start))
        } else {
            localStorage.removeItem(OFFLINE_SINCE_STORAGE_KEY)
        }
    } catch {
    }
}

export default defineNuxtPlugin((nuxtApp) => {
    setTelemetryClient(nuxtApp.$appInsights as ApplicationInsights | undefined)

    const currentRoute = (): string => window.location.pathname

    const onOffline = (): void => {
        if (!readOfflineStart()) {
            writeOfflineStart({startedAt: Date.now(), route: currentRoute()})
        }
    }

    const onOnline = (endedOnRestart: boolean): void => {
        const start = readOfflineStart()
        if (start) {
            writeOfflineStart(undefined)
            trackEvent(TELEMETRY_EVENT.offlinePeriod, {
                durationSeconds: Math.round((Date.now() - start.startedAt) / 1000),
                route: start.route,
                routeAtEnd: currentRoute(),
                endedOnRestart,
            })
        }
        flushTelemetryBacklog()
    }

    window.addEventListener('offline', onOffline)
    window.addEventListener('online', () => onOnline(false))

    if (navigator.onLine) {
        onOnline(true)
    } else {
        trackEvent(TELEMETRY_EVENT.appStartedOffline, {route: currentRoute()})
        onOffline()
    }
})
